import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Book, Sparkles, Leaf, Heart, Eye } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SocialShareBar = ({ url, title, summary }) => {
  const encodedUrl = encodeURIComponent(url);
  const shareText = summary || "Read this memoir on My Story";
  const encodedShareText = encodeURIComponent(shareText);

  const platforms = [
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${encodedShareText}%0A%0A${encodedUrl}`,
      color: "bg-[#25D366] hover:bg-[#1ebe57]",
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedShareText}`,
      color: "bg-[#1877F2] hover:bg-[#0d6ce8]",
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
    },
    {
      name: "X",
      href: `https://twitter.com/intent/tweet?text=${encodedShareText}&url=${encodedUrl}`,
      color: "bg-[#0f1419] hover:bg-[#272d33]",
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
    },
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      color: "bg-[#0A66C2] hover:bg-[#0857a6]",
      icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
    }
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {}
  };

  return (
    <div className="flex items-center gap-2" data-testid="share-social-bar">
      {platforms.map(p => (
        <a key={p.name} href={p.href} target="_blank" rel="noopener noreferrer" className={`${p.color} text-white p-2 rounded-xl transition-colors`} title={`Share on ${p.name}`} data-testid={`share-view-${p.name.toLowerCase()}`}>
          {p.icon}
        </a>
      ))}
      <button onClick={handleCopy} className="bg-[#4A5D4A] text-white p-2 rounded-xl hover:bg-[#2D3A2D] transition-colors" title="Copy link" data-testid="share-view-copy">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      </button>
    </div>
  );
};

const ShareView = () => {
  const { shareToken } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchStory = async () => {
      try {
        const response = await axios.get(`${API}/story/public/${shareToken}`);
        setStory(response.data);
        // Track view (one per session per token)
        const viewedKey = `viewed_${shareToken}`;
        if (!sessionStorage.getItem(viewedKey)) {
          axios.post(`${API}/story/view/${shareToken}`).catch(() => {});
          sessionStorage.setItem(viewedKey, "1");
        }
      } catch (err) {
        setError(err.response?.status === 404 ? "Story not found" : "Failed to load story");
      } finally {
        setLoading(false);
      }
    };
    fetchStory();
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#7A9B7E] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-sans text-[#7A8A7A]">Loading memoir...</p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center p-8">
        <div className="text-center max-w-md" data-testid="share-error">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E5D4C1]/40 flex items-center justify-center">
            <Book className="w-10 h-10 text-[#8A7A6A]" />
          </div>
          <h2 className="font-serif text-3xl text-[#4A5D4A] mb-3">{error || "Not found"}</h2>
          <p className="font-sans text-[#7A8A7A] mb-6">This memoir may have been removed or the link is invalid.</p>
          <Link to="/login" className="inline-flex items-center gap-2 bg-[#7A9B7E] text-white px-6 py-3 rounded-full font-sans hover:bg-[#6A8B6E] transition-all">
            <Leaf className="w-4 h-4" />
            Create Your Own Story
          </Link>
        </div>
      </div>
    );
  }

  const { author_name, author_picture, book_title, genre, chapters, categorized_chapters, summary, social_summary, template: templateId, cover_image: coverImage, likes: initialLikes = 0, views: initialViews = 0 } = story;
  const categories = categorized_chapters ? Object.keys(categorized_chapters) : [];
  const displayChapters = selectedCategory === "all" ? chapters : (categorized_chapters[selectedCategory] || []);
  const ogShareUrl = `${BACKEND_URL}/api/og/share/${shareToken}`;
  const shareTitle = `${book_title || "My Story"} by ${author_name}`;
  const shareText = social_summary || summary;

  const THEMES = {
    freestyle: { gf: "#9FB89F", gt: "#5A7B5E" }, family_history: { gf: "#A67C2E", gt: "#6B4F12" },
    travel_journal: { gf: "#1E40AF", gt: "#3B82F6" }, career: { gf: "#1E3A5F", gt: "#2D5F8A" },
    childhood: { gf: "#B45309", gt: "#D97706" }, love_story: { gf: "#9D174D", gt: "#BE185D" },
    personal_growth: { gf: "#047857", gt: "#059669" }, adventure: { gf: "#92400E", gt: "#B45309" },
    immigration: { gf: "#3730A3", gt: "#4338CA" }, war_service: { gf: "#334155", gt: "#475569" },
  };
  const t = THEMES[templateId] || THEMES.freestyle;

  return (
    <div className="min-h-screen bg-[#F8F6F1] relative" data-testid="share-view">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#9FB89F]/5 organic-blob" />
      <div className="absolute bottom-40 left-20 w-[400px] h-[400px] bg-[#E5D4C1]/10 organic-blob" style={{ animationDelay: '3s' }} />

      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white/60 backdrop-blur-md border-b border-[#D4C9B8]/30 px-6 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif text-sm text-[#7A8A7A]">My Story</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <SocialShareBar url={ogShareUrl} title={shareTitle} summary={shareText} />
            <Link
              to="/login"
              className="text-xs font-sans bg-[#4A5D4A] text-white px-4 py-2 rounded-full hover:bg-[#2D3A2D] transition-colors ml-2"
              data-testid="share-create-own"
            >
              Create Your Own
            </Link>
          </div>
        </div>
      </div>

      {/* Category filter */}
      {categories.length > 1 && (
        <div className="max-w-5xl mx-auto px-6 pt-4 flex gap-2 flex-wrap">
          <button onClick={() => setSelectedCategory("all")} className={`px-3 py-1.5 rounded-full text-xs font-sans transition-all ${selectedCategory === "all" ? "bg-[#7A9B7E] text-white" : "bg-white/80 text-[#7A8A7A] hover:bg-[#E5D4C1]/50"}`}>
            All
          </button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-3 py-1.5 rounded-full text-xs font-sans transition-all ${selectedCategory === cat ? "bg-[#9FB89F] text-white" : "bg-white/80 text-[#7A8A7A] hover:bg-[#E5D4C1]/50"}`}>
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Book */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white/80 backdrop-blur-sm shadow-2xl border-2 border-[#D4C9B8]/40 rounded-[2rem] overflow-hidden">
          {/* Cover */}
          <div className="relative text-center overflow-hidden" style={{ minHeight: coverImage ? '320px' : undefined }}>
            {coverImage ? (
              <>
                <img src={coverImage} alt={book_title} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${t.gf}CC, ${t.gt}CC)` }} />
              </>
            ) : (
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${t.gf}, ${t.gt})` }}>
                <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 organic-blob" />
                <div className="absolute bottom-10 right-10 w-48 h-48 bg-white/10 organic-blob" style={{ animationDelay: '2s' }} />
              </div>
            )}
            <div className="relative z-10 p-16">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-serif font-bold text-5xl lg:text-7xl text-white mb-4 tracking-tight drop-shadow-lg">
              {book_title || "My Story"}
            </motion.h1>
            <p className="font-sans text-lg text-white/90 mb-2">A Personal Memoir</p>
            {genre && <span className="inline-block font-sans text-xs text-white/70 bg-white/20 px-4 py-1 rounded-full mt-2">{genre}</span>}
            <div className="flex items-center justify-center gap-3 mt-6">
              {author_picture && (
                <img src={author_picture} alt={author_name} className="w-10 h-10 rounded-full border-2 border-white/50" />
              )}
              <span className="font-sans text-white/80 text-sm">by {author_name}</span>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 text-white/70">
              <Sparkles className="w-4 h-4" />
              <span className="font-sans text-xs">{chapters.length} Chapter{chapters.length !== 1 ? 's' : ''}</span>
            </div>
            </div>
          </div>

          {/* Chapters */}
          <div className="p-10 lg:p-20">
            <AnimatePresence mode="wait">
              <div className="space-y-20">
                {displayChapters.map((chapter, index) => (
                  <motion.div key={chapter.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="relative">
                    <div className="mb-6">
                      {chapter.category && (
                        <span className="inline-block px-4 py-1.5 bg-[#9FB89F]/20 text-[#5A7B5E] text-xs font-sans font-medium rounded-full mb-3 border border-[#9FB89F]/30">{chapter.category}</span>
                      )}
                      <h2 className="font-serif font-bold text-3xl lg:text-4xl text-[#4A5D4A] mb-2">{chapter.title}</h2>
                      {chapter.time_period && <p className="font-sans text-sm text-[#7A8A7A] italic">{chapter.time_period}</p>}
                    </div>

                    {chapter.image_url && (
                      <div className="mb-8">
                        <img src={chapter.image_url} alt={chapter.title} className="w-full max-w-3xl h-96 object-cover rounded-3xl shadow-lg border-4 border-white" />
                      </div>
                    )}

                    <div className="font-sans text-lg text-[#4A5D4A] leading-relaxed">
                      <div className="first-letter:text-7xl first-letter:font-serif first-letter:font-bold first-letter:text-[#7A9B7E] first-letter:float-left first-letter:mr-3">{chapter.content}</div>
                    </div>

                    {chapter.themes && chapter.themes.length > 0 && (
                      <div className="mt-6 flex flex-wrap gap-2">
                        {chapter.themes.map((theme, i) => (
                          <span key={i} className="px-3 py-1 bg-[#E5D4C1]/40 text-[#7A6A5A] text-xs font-sans rounded-full">{theme}</span>
                        ))}
                      </div>
                    )}

                    {index < displayChapters.length - 1 && (
                      <div className="mt-20 flex justify-center">
                        <div className="w-32 h-px bg-gradient-to-r from-transparent via-[#D4C9B8] to-transparent" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>

            {/* Footer with social share */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-24 text-center border-t border-[#D4C9B8]/50 pt-12">
              <p className="font-serif text-2xl italic text-[#7A8A7A] mb-4">The End</p>

              {/* Engagement bar — Like + Views */}
              <EngagementBar shareToken={shareToken} initialLikes={initialLikes} initialViews={initialViews} />

              <p className="font-sans text-sm text-[#7A8A7A] mb-4 mt-6">Enjoyed this story? Share it!</p>
              <div className="flex justify-center mb-8">
                <SocialShareBar url={ogShareUrl} title={shareTitle} summary={shareText} />
              </div>

              <p className="font-sans text-sm text-[#A0AEC0] mb-6">Created with My Story</p>
              <Link to="/login" className="inline-flex items-center gap-2 bg-[#7A9B7E] text-white px-6 py-3 rounded-full font-sans text-sm hover:bg-[#6A8B6E] transition-all shadow-md">
                <Leaf className="w-4 h-4" />
                Write Your Own Memoir
              </Link>
            </motion.div>

            {/* More by this author */}
            <MoreByAuthor shareToken={shareToken} />

            {/* Comments Section */}
            <CommentsSection shareToken={shareToken} />

          </div>
        </div>
      </div>
    </div>
  );
};

const EngagementBar = ({ shareToken, initialLikes, initialViews }) => {
  const [likes, setLikes] = useState(initialLikes);
  const likedKey = `liked_${shareToken}`;
  const [liked, setLiked] = useState(() => !!localStorage.getItem(likedKey));
  const [pulsing, setPulsing] = useState(false);

  const handleLike = async () => {
    if (liked) return;
    setLiked(true);
    setPulsing(true);
    setLikes(prev => prev + 1);
    localStorage.setItem(likedKey, "1");
    try {
      const res = await axios.post(`${API}/story/like/${shareToken}`);
      if (typeof res.data?.likes === "number") setLikes(res.data.likes);
    } catch {
      // rollback on failure
      setLiked(false);
      setLikes(prev => Math.max(0, prev - 1));
      localStorage.removeItem(likedKey);
    }
    setTimeout(() => setPulsing(false), 600);
  };

  return (
    <div className="flex items-center justify-center gap-4" data-testid="engagement-bar">
      <button
        onClick={handleLike}
        disabled={liked}
        className={`group flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-sm transition-all border ${
          liked
            ? "bg-rose-50 text-rose-600 border-rose-200 cursor-default"
            : "bg-white text-[#4A5D4A] border-[#D4C9B8]/60 hover:border-rose-200 hover:text-rose-500 hover:shadow-sm"
        }`}
        data-testid="like-button"
        aria-label={liked ? "You liked this story" : "Like this story"}
      >
        <Heart className={`w-4 h-4 transition-all ${liked ? "fill-rose-500 text-rose-500" : "group-hover:text-rose-500"} ${pulsing ? "scale-125" : ""}`} />
        <span data-testid="like-count">{likes}</span>
        <span className="hidden sm:inline">{liked ? "Liked" : "Like"}</span>
      </button>
      <div className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/60 border border-[#D4C9B8]/40 text-[#7A8A7A] font-sans text-sm" data-testid="view-count-pill">
        <Eye className="w-4 h-4" />
        <span data-testid="view-count">{initialViews}</span>
        <span className="hidden sm:inline">views</span>
      </div>
    </div>
  );
};

const MoreByAuthor = ({ shareToken }) => {
  const [data, setData] = useState({ author_name: null, author_picture: null, stories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/story/public/${shareToken}/more-by-author`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shareToken]);

  if (loading || !data.stories || data.stories.length === 0) return null;

  return (
    <section className="mt-20 pt-12 border-t border-[#D4C9B8]/50" data-testid="more-by-author">
      <div className="text-center mb-8">
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#7A9B7E] mb-2">More from this author</p>
        <h3 className="font-serif text-3xl text-[#2D3A2D] flex items-center justify-center gap-3">
          {data.author_picture && (
            <img src={data.author_picture} alt={data.author_name} className="w-9 h-9 rounded-full border-2 border-[#D4C9B8]/60" />
          )}
          More by {data.author_name}
        </h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.stories.map((s) => (
          <Link
            key={s.book_id}
            to={`/share/${s.share_token}`}
            className="group block bg-white/80 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            data-testid={`more-story-${s.book_id}`}
          >
            <div className="h-36 relative overflow-hidden">
              {s.cover_image ? (
                <img src={s.cover_image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#9FB89F] to-[#5A7B5E] flex items-center justify-center">
                  <Book className="w-10 h-10 text-white/70" />
                </div>
              )}
              {s.genre && (
                <span className="absolute top-3 left-3 text-[10px] font-sans bg-white/90 text-[#4A5D4A] px-2.5 py-1 rounded-full">
                  {s.genre}
                </span>
              )}
            </div>
            <div className="p-4">
              <h4 className="font-serif text-lg text-[#2D3A2D] mb-1.5 line-clamp-2 group-hover:text-[#4A5D4A]">
                {s.first_chapter_title || s.title}
              </h4>
              {s.summary && (
                <p className="font-sans text-xs text-[#7A8A7A] leading-relaxed line-clamp-3 mb-3">
                  {s.summary}
                </p>
              )}
              <div className="flex items-center gap-3 text-[11px] font-sans text-[#9FB89F]">
                <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" />{s.chapter_count} ch.</span>
                <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{s.likes}</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{s.views}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const CommentsSection = ({ shareToken }) => {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/story/comments/${shareToken}`).then(r => setComments(r.data.comments || [])).catch(() => {});
  }, [shareToken]);

  const handlePost = async () => {
    if (!name.trim() || !text.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/story/comments/${shareToken}`, { name: name.trim(), text: text.trim() });
      if (res.data.comment) {
        setComments(prev => [res.data.comment, ...prev]);
        setText("");
      }
    } catch { /* ignore */ }
    setPosting(false);
  };

  return (
    <div className="mt-16 max-w-2xl mx-auto" data-testid="comments-section">
      <h3 className="font-serif text-2xl text-[#2D3A2D] mb-6 text-center">Comments</h3>

      {/* Post form */}
      <div className="bg-white rounded-xl border border-[#E5D4C1]/40 p-5 mb-6 space-y-3">
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
          className="w-full px-4 py-2.5 rounded-lg border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
          data-testid="comment-name" />
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Share your thoughts..." rows={3}
          className="w-full px-4 py-2.5 rounded-lg border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40 resize-none"
          data-testid="comment-text" />
        <button onClick={handlePost} disabled={posting || !name.trim() || !text.trim()}
          className="px-6 py-2 rounded-full bg-[#4A5D4A] text-white font-sans text-sm font-medium hover:bg-[#2D3A2D] transition-all disabled:opacity-50"
          data-testid="comment-submit">
          {posting ? "Posting..." : "Post Comment"}
        </button>
      </div>

      {/* List */}
      {comments.length === 0 ? (
        <p className="text-center font-sans text-sm text-[#9FB89F]">No comments yet. Be the first!</p>
      ) : (
        <div className="space-y-4">
          {comments.map(c => (
            <div key={c.id} className="bg-white/60 rounded-xl border border-[#E5D4C1]/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-sans text-sm font-medium text-[#2D3A2D]">{c.name}</span>
                <span className="font-sans text-[10px] text-[#9FB89F]">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="font-sans text-sm text-[#4A5D4A] leading-relaxed">{c.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShareView;
